/*
  Saffiome cart upsell. Drives the <saffiome-cart-upsell> card that snippets/saffiome-cart-upsell.liquid
  renders inside the cart drawer. Liquid decides what to offer and computes every amount; this file only
  performs the switch:
    1. POST routes.cart_add_url    { items: [{ id, quantity, selling_plan? }], sections, sections_url }
    2. POST routes.cart_change_url { id: <line key>, quantity: <what that line keeps>, sections, sections_url }, one per source line
    3. publish cart-update (source 'saffiome-cart-upsell'), then CartDrawer.renderContents(last response)
       so the drawer and the header cart bubble repaint from the returned sections.
  The add runs first so a failure never loses the shopper's box. Errors re-enable the control and speak
  through a live region. Without JavaScript the accept control is a plain link to the target product page.
  Fires document "saffiome:event" with { name: 'cart upsell accepted', from, to, offer } (and 'cart upsell dismissed').
*/
if (!customElements.get('saffiome-cart-upsell')) {
  const DISMISSED_KEY = 'sf-cart-upsell-dismissed';

  class SaffiomeCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.onAccept = this.onAccept.bind(this);
      this.onDismiss = this.onDismiss.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.accept = this.querySelector('[data-sf-upsell-accept]');
      this.dismiss = this.querySelector('[data-sf-upsell-dismiss]');
      this.status = this.querySelector('[data-sf-upsell-status]');
      this.offerKey = this.dataset.sfKey || `${this.dataset.sfFrom}>${this.dataset.sfTo}`;
      if (this.wasDismissed()) {
        this.hidden = true;
        return;
      }
      if (this.accept) {
        this.accept.setAttribute('role', 'button');
        this.accept.addEventListener('click', this.onAccept);
        this.accept.addEventListener('keydown', this.onKeydown);
      }
      if (this.dismiss) this.dismiss.addEventListener('click', this.onDismiss);
    }

    disconnectedCallback() {
      if (this.accept) {
        this.accept.removeEventListener('click', this.onAccept);
        this.accept.removeEventListener('keydown', this.onKeydown);
      }
      if (this.dismiss) this.dismiss.removeEventListener('click', this.onDismiss);
    }

    wasDismissed() {
      try {
        return (sessionStorage.getItem(DISMISSED_KEY) || '').split('|').includes(this.offerKey);
      } catch (e) {
        return false;
      }
    }

    remember() {
      try {
        const list = (sessionStorage.getItem(DISMISSED_KEY) || '').split('|').filter(Boolean);
        if (!list.includes(this.offerKey)) list.push(this.offerKey);
        sessionStorage.setItem(DISMISSED_KEY, list.join('|'));
      } catch (e) {
        /* No storage: the card simply comes back on the next render. */
      }
    }

    onKeydown(event) {
      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        this.accept.click();
      }
    }

    onDismiss() {
      this.remember();
      const inner = this.closest('.drawer__inner');
      this.hidden = true;
      if (inner) inner.focus();
      this.emit('cart upsell dismissed');
    }

    async onAccept(event) {
      event.preventDefault();
      if (this.busy) return;
      this.setBusy(true);
      this.say('');

      const drawer = document.querySelector('cart-drawer');
      const sections =
        drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((section) => section.id)
          : ['cart-drawer', 'cart-icon-bubble'];
      const common = { sections, sections_url: window.location.pathname };
      const line = { id: Number(this.dataset.sfVariant), quantity: Number(this.dataset.sfQuantity) || 1 };
      if (this.dataset.sfPlan) line.selling_plan = Number(this.dataset.sfPlan);
      let changes = [];
      try {
        changes = JSON.parse(this.dataset.sfChanges || '[]');
      } catch (e) {
        changes = [];
      }

      let state;
      try {
        state = await this.post(window.routes.cart_add_url, Object.assign({ items: [line] }, common));
      } catch (error) {
        this.setBusy(false);
        this.say(error.message || this.dataset.sfError);
        return;
      }

      try {
        for (const change of changes) {
          state = await this.post(
            window.routes.cart_change_url,
            Object.assign({ id: change.key, quantity: change.quantity }, common)
          );
        }
      } catch (error) {
        this.paint(drawer, state);
        this.notice(this.dataset.sfErrorPartial || error.message, true);
        return;
      }

      this.paint(drawer, state);
      this.notice(this.dataset.sfDone, false);
      this.emit('cart upsell accepted');
    }

    post(url, body) {
      const config =
        typeof fetchConfig === 'function'
          ? fetchConfig('json')
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      config.body = JSON.stringify(body);
      return fetch(url, config).then((response) =>
        response
          .json()
          .catch(() => ({}))
          .then((data) => {
            if (!response.ok || data.status) {
              throw new Error(data.description || data.message || this.dataset.sfError || 'Something went wrong.');
            }
            return data;
          })
      );
    }

    /* Repaint the way Dawn does after an add: tell subscribers, then render the returned sections in place. */
    paint(drawer, state) {
      if (!state) return;
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'saffiome-cart-upsell', cartData: state });
      }
      if (!drawer || typeof drawer.renderContents !== 'function' || !state.sections) return;
      drawer.renderContents(state);
      const inner = drawer.querySelector('.drawer__inner');
      if (inner) {
        if (typeof trapFocus === 'function') trapFocus(document.getElementById('CartDrawer') || drawer, inner);
        else inner.focus();
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        bubble.classList.remove('is-bumped');
        void bubble.offsetWidth;
        bubble.classList.add('is-bumped');
        setTimeout(() => bubble.classList.remove('is-bumped'), 640);
      }
    }

    /* Message that outlives this card (the drawer has just been re-rendered): errors visible, success for screen readers. */
    notice(text, isError) {
      if (!text) return;
      const footer = document.querySelector('cart-drawer .drawer__footer');
      if (!footer) return;
      footer.querySelectorAll('.sf-upsell__notice').forEach((old) => old.remove());
      const p = document.createElement('p');
      p.className = isError ? 'sf-upsell__notice' : 'sf-upsell__notice sf-sr';
      p.setAttribute('role', isError ? 'alert' : 'status');
      p.textContent = text;
      footer.prepend(p);
      if (!isError) setTimeout(() => p.remove(), 4000);
    }

    setBusy(on) {
      this.busy = on;
      if (this.accept) this.accept.setAttribute('aria-disabled', on ? 'true' : 'false');
      if (this.dismiss) this.dismiss.disabled = on;
    }

    say(text) {
      if (this.status) this.status.textContent = text || '';
    }

    emit(name) {
      document.dispatchEvent(
        new CustomEvent('saffiome:event', {
          detail: { name, from: this.dataset.sfFrom, to: this.dataset.sfTo, offer: this.dataset.sfOffer },
        })
      );
    }
  }

  customElements.define('saffiome-cart-upsell', SaffiomeCartUpsell);
}
